import React, { useMemo } from 'react';
import { Modal, Card, Collapse } from 'antd';
import { FileSearchOutlined, CloseOutlined } from '@ant-design/icons';
import { isEmpty, processMktuClasses } from '../../../utils/helpers';

const { Panel } = Collapse;

/**
 * RequestModal – подробная карточка заявки на товарный знак.
 * Картинку показываем ТОЛЬКО если она уже пришла в виде data:URI.
 * Никаких дополнительных загрузок по внешним URL, чтобы избежать блокировок.
 */

// ---------------------------------------------------------------------- словарь читаемых меток
const labelMap = {
  realName: 'Название',
  '210_Номер_заявки': 'Номер заявки',
  url_210_Номер_заявки: 'Ссылка на заявку',
  '200_Дата_поступления_заявки': 'Дата поступления заявки',
  '220_Дата_подачи_заявки': 'Дата подачи заявки',
  '441_Опубликовано': 'Опубликовано',
  url_441_Опубликовано: 'Ссылка на публикацию',
  '190_Страна': 'Страна',
  '511_КлассыМКТУ': 'Классы МКТУ',
  '511_Классы_МКТУ_и_перечень_товаров_и/или_услуг': 'Классы МКТУ и товары/услуги',
  'Состояние_делопроизводства': 'Состояние делопроизводства',
  Статус: 'Статус',
  img_url: 'Ссылка на изображение',
  '540_Изображение_(воспроизведение)_заявляемого_обозначения': 'Текстовое описание изображения',
  'url_540_Изображение_(воспроизведение)_заявляемого_обозначения': 'Ссылка на изображение',
  '731_Заявитель': 'Заявитель',
  '740_Патентный_поверенный_(полное_имя,_регистрационный_номер,_местонахождение)': 'Патентный поверенный',
  '750_Адрес_для_переписки': 'Адрес для переписки',
};

// ---------------------------------------------------------------------- компонент
const RequestModal = ({ open, onClose, doc, imgSrc }) => {
  // Проверка на истекший срок действия
  const isExpired = useMemo(() => {
    if (!doc) return false;
    const document = doc.document || doc;
    const expirationDate = document?.['181_dataIstIsklPrava'] || document?.expiration_date;
    if (!expirationDate) return false;
    
    // Обрабатываем разные форматы дат
    let dateToParse = expirationDate;
    
    // Если дата в массиве, берем первый элемент
    if (Array.isArray(expirationDate)) {
      dateToParse = expirationDate[0];
    }
    
    // Если дата в формате DD.MM.YYYY, преобразуем в YYYY-MM-DD
    if (typeof dateToParse === 'string' && dateToParse.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      const [day, month, year] = dateToParse.split('.');
      dateToParse = `${year}-${month}-${day}`;
    }
    
    const expiration = new Date(dateToParse);
    const now = new Date();
    
    // Проверяем, что дата валидна
    if (isNaN(expiration.getTime())) {
      return false;
    }
    
    return expiration < now;
  }, [doc]);

  if (!doc) return null;

  // объект заявки может прийти как { document, ... }
  const document = doc.document || doc;

  const renderValue = (key, value) => {
    if ((key.startsWith('url_') || key === 'img_url') && value) {
      // Для ссылок берем первый элемент массива или само значение
      const linkValue = Array.isArray(value) ? value[0] : value;
      if (linkValue) {
        return (
          <a href={linkValue} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', textDecoration: 'underline' }}>Открыть</a>
        );
      }
    }
    
    if (Array.isArray(value)) {
      return (
        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
          {value.map((item, idx) => (
            <li key={idx} style={{ listStyle: 'disc' }}>{item}</li>
          ))}
        </ul>
      );
    }
    
    return value ?? '—';
  };

  const renderField = (key, value) => {
    if (isEmpty(value)) return null;
    
    // Проверяем, является ли это полем даты истечения и истек ли срок
    const isExpirationField = key === '181_dataIstIsklPrava' || key === 'expiration_date';
    const isExpiredField = isExpirationField && isExpired;
    
    return (
      <div
        className={isExpiredField ? 'theme-error-field' : undefined}
        style={{
          marginBottom: 8,
          padding: '8px',
          borderRadius: '4px',
        }}
        key={key}
      >
        <b style={{ color: isExpiredField ? '#cf1322' : undefined }} className={!isExpiredField ? 'theme-detail-field-label' : undefined}>
          {labelMap[key] || key}:
          {isExpiredField && ' ⚠️ ИСТЕК СРОК!'}
        </b> {renderValue(key, value)}
      </div>
    );
  };

  const mktuInfo = processMktuClasses(document['511_КлассыМКТУ'] || document['511_Классы_МКТУ_и_перечень_товаров_и/или_услуг']);

  // AI-данные (elements, colors, text_content, semantic_description)
  const aiData = doc?.document || doc || document;
  const hasAiSection = aiData && (
    (Array.isArray(aiData.elements) && aiData.elements.length > 0) ||
    (Array.isArray(aiData.colors) && aiData.colors.length > 0) ||
    (Array.isArray(aiData.text_content) && aiData.text_content.length > 0) ||
    (typeof aiData.semantic_description === 'string' && aiData.semantic_description.trim() !== '')
  );

  // Группы полей
  const mainFields = [
    'realName', '210_Номер_заявки', 'Состояние_делопроизводства', 'Статус', '190_Страна', '731_Заявитель', '750_Адрес_для_переписки', '740_Патентный_поверенный_(полное_имя,_регистрационный_номер,_местонахождение)'
  ];
  const dateFields = [
    '200_Дата_поступления_заявки', '220_Дата_подачи_заявки', '441_Опубликовано'
  ];
  const linkFields = [
    'url_210_Номер_заявки', 'url_441_Опубликовано', 'img_url', 'url_540_Изображение_(воспроизведение)_заявляемого_обозначения'
  ];
  const descFields = [
    '540_Изображение_(воспроизведение)_заявляемого_обозначения'
  ];

  // ---------------- JSX ----------------
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width="min(960px, 92vw)"
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', width: '100%' }}
      closeIcon={<CloseOutlined style={{ fontSize: 20 }} />}
    >
      <Card
        title={
          <div className="theme-modal-title">
            {document?.realName || 'Без названия'}
          </div>
        }
        cover={
          imgSrc ? (
            <div style={{ textAlign: 'center', padding: '20px 0', maxWidth: '100%', boxSizing: 'border-box' }}>
              <img
                src={imgSrc}
                alt={document?.realName || 'Без названия'}
                style={{
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  maxHeight: 'min(72vh, 85vw)',
                  borderRadius: 8,
                  objectFit: 'contain',
                }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <FileSearchOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </div>
          )
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Основная информация */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">📋 Основная информация</h4>
            {mainFields.map((k) => renderField(k, document[k]))}
          </div>
          {/* Даты */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">📅 Даты</h4>
            {dateFields.map((k) => renderField(k, document[k]))}
          </div>
          {/* Ссылки */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">🔗 Ссылки</h4>
            {linkFields.map((k) => renderField(k, document[k]))}
          </div>
          {/* Классы МКТУ */}
          {mktuInfo.numbers.length > 0 && (
            <div className="theme-detail-section">
              <h4 className="theme-detail-section__title">🏷️ Классы МКТУ</h4>
              <div style={{ marginBottom: 8, padding: '4px 0' }}>
                <b className="theme-detail-field-label">Классы:</b> {mktuInfo.numbers.join(', ')}
              </div>
              <Collapse style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <Panel header="Показать полную расшифровку" key="mktu-panel">
                  {renderValue('511_КлассыМКТУ', document['511_КлассыМКТУ'] || document['511_Классы_МКТУ_и_перечень_товаров_и/или_услуг'])}
                </Panel>
              </Collapse>
            </div>
          )}
          {/* Описание изображения */}
          {descFields.some((k) => !isEmpty(document[k])) && (
            <div className="theme-detail-section">
              <h4 className="theme-detail-section__title">🖼️ Описание изображения</h4>
              {descFields.map((k) => renderField(k, document[k]))}
            </div>
          )}
          {/* AI */}
          {hasAiSection && (
            <div className="theme-detail-section">
              <h4 className="theme-detail-section__title">🤖 AI</h4>
              {Array.isArray(aiData.elements) && aiData.elements.length > 0 && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Элементы (ИИ):</b> {renderValue('', aiData.elements)}
                </div>
              )}
              {Array.isArray(aiData.colors) && aiData.colors.length > 0 && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Цвета (ИИ):</b> {renderValue('', aiData.colors)}
                </div>
              )}
              {Array.isArray(aiData.text_content) && aiData.text_content.length > 0 && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Текстовое содержание (ИИ):</b> {renderValue('', aiData.text_content)}
                </div>
              )}
              {typeof aiData.semantic_description === 'string' && aiData.semantic_description.trim() !== '' && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Семантическое описание (ИИ):</b> {aiData.semantic_description.trim()}
                </div>
              )}
            </div>
          )}
          {/* Делопроизводство */}
          {!isEmpty(document['Делопроизводство']) && (
            <div className="theme-detail-section">
              <h4 className="theme-detail-section__title">📄 Делопроизводство</h4>
              <Collapse style={{ maxHeight: 200, overflowY: 'auto' }}>
                <Panel header="Показать / скрыть" key="delo-panel">
                  <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                    {Object.entries(document['Делопроизводство']).map(([act, dates]) =>
                      dates.map((d, i) => (
                        <li key={`${act}_${i}`} style={{ listStyle: 'disc' }}>
                          <strong>{act}:</strong> {d}
                        </li>
                      )),
                    )}
                  </ul>
                </Panel>
              </Collapse>
            </div>
          )}
        </div>
      </Card>
    </Modal>
  );
};

export default RequestModal;
